import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type AccountSummary,
  deriveAccountStatus,
  formatAccountNo,
} from "@/lib/accounts";
import { DetailDrawer } from "@/components/ui/drawer";
import { type TabItem } from "@/components/ui/tabs";
import { AccountOverviewTab } from "@/components/accounts/AccountOverviewTab";
import { AccountSettingsTab } from "@/components/accounts/AccountSettingsTab";
import { AccountSecurityTab } from "@/components/accounts/AccountSecurityTab";
import { AccountLogsTab } from "@/components/accounts/AccountLogsTab";

/**
 * AccountDetailDrawer — the former expanded `<tr>` monster, now a tabbed
 * DetailDrawer (Обзор / Настройки / Безопасность / Логи). Drawer is local
 * state over the list (no new route), per the brief.
 */
export function AccountDetailDrawer({
  account,
  open,
  onOpenChange,
}: {
  account: AccountSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("overview");

  if (!account) return null;

  const displayStatus = deriveAccountStatus(account);
  const title = account.tg_username ? `@${account.tg_username}` : account.display_name;

  const tabs: TabItem[] = [
    {
      value: "overview",
      label: t("accounts.tab.overview"),
      content: <AccountOverviewTab account={account} displayStatus={displayStatus} />,
    },
    {
      value: "settings",
      label: t("accounts.tab.settings"),
      content: <AccountSettingsTab account={account} />,
    },
    {
      value: "security",
      label: t("accounts.tab.security"),
      content: <AccountSecurityTab account={account} />,
    },
    {
      value: "logs",
      label: t("accounts.tab.logs"),
      content: <AccountLogsTab account={account} />,
    },
  ];

  return (
    <DetailDrawer
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) setActiveTab("overview");
      }}
      title={
        <>
          <span className="font-mono text-fg-muted">{formatAccountNo(account.seq_no)}</span>{" "}
          {title}
        </>
      }
      breadcrumbs={[
        { label: t("accounts.title") },
        { label: title },
      ]}
      status={{
        domain: "account",
        value: displayStatus,
        label: t(`accounts.status.${displayStatus}`),
      }}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      size="lg"
    />
  );
}
